// @ts-nocheck
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@libraryadmin/components/layout/DashboardLayout";
import { Button } from "@libraryadmin/components/ui/button";
import { usePagination } from "@libraryadmin/hooks/usePagination";
import { DataTablePagination } from "@libraryadmin/components/ui/data-table-pagination";
import { Input } from "@libraryadmin/components/ui/input";
import { Label } from "@libraryadmin/components/ui/label";
import { Badge } from "@libraryadmin/components/ui/badge";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@libraryadmin/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@libraryadmin/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@libraryadmin/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@libraryadmin/components/ui/select";
import { Skeleton } from "@libraryadmin/components/ui/skeleton";
import {
  IndianRupee,
  Clock,
  BookX,
  AlertTriangle,
  UserX,
  Users,
  Search,
  CheckCircle2,
  XCircle,
  Plus,
  Phone,
  Calendar,
  Receipt,
  RotateCcw,
  Trash2,
} from "lucide-react";
import { useToast } from "@libraryadmin/hooks/use-toast";
import {
  getFines,
  payFine,
  waiveFine,
  createFine,
  deleteFine,
  getMembers,
  getBooks,
} from "@libraryadmin/services/api";
import type { FineItem } from "@libraryadmin/data/seed";

const damageBadgeStyle: Record<string, string> = {
  minor: "badge-warning",
  moderate: "badge-destructive",
  severe: "bg-red-600 text-white",
};

const statusBadgeStyle: Record<string, string> = {
  pending: "badge-destructive",
  paid: "badge-success",
  waived: "badge-secondary",
};

export default function FineManagement() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<string>("member_payments");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  // Selected Fine for Payment / Waiver
  const [payingFine, setPayingFine] = useState<FineItem | null>(null);
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [deletingFine, setDeletingFine] = useState<FineItem | null>(null);

  // Add Fine Dialog
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newFineForm, setNewFineForm] = useState({
    category: "late_return" as FineItem["category"],
    memberId: "",
    memberName: "",
    phone: "",
    bookNo: "",
    bookTitle: "",
    amount: 100,
    remarks: "",
  });

  const { data: fines = [], isLoading } = useQuery({
    queryKey: ["fines"],
    queryFn: getFines,
  });

  const { data: members = [] } = useQuery({
    queryKey: ["members"],
    queryFn: getMembers,
  });

  const { data: books = [] } = useQuery({
    queryKey: ["books"],
    queryFn: () => getBooks(),
  });

  // Mutations
  const payMutation = useMutation({
    mutationFn: ({ id, method }: { id: string; method: string }) =>
      payFine(id, method),
    onSuccess: updated => {
      queryClient.invalidateQueries({ queryKey: ["fines"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });
      setPayingFine(null);
      toast({
        title: "Fine Payment Recorded",
        description: `₹${updated.amount} received from ${updated.memberName} (${updated.fineNo}).`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Unable to Record Payment",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const waiveMutation = useMutation({
    mutationFn: (id: string) => waiveFine(id, "Waived by librarian"),
    onSuccess: updated => {
      queryClient.invalidateQueries({ queryKey: ["fines"] });
      toast({
        title: "Fine Waived",
        description: `Fine ${updated.fineNo} for ${updated.memberName} was waived.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Unable to Waive Fine",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const addFineMutation = useMutation({
    mutationFn: createFine,
    onSuccess: newFine => {
      queryClient.invalidateQueries({ queryKey: ["fines"] });
      setShowAddDialog(false);
      setNewFineForm({
        category: "late_return",
        memberId: "",
        memberName: "",
        phone: "",
        bookNo: "",
        bookTitle: "",
        amount: 100,
        remarks: "",
      });
      toast({
        title: "Fine Recorded Successfully",
        description: `Fine ${newFine.fineNo} of ₹${newFine.amount} registered for ${newFine.memberName}.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Unable to Create Payment",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteFine,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fines"] });
      setDeletingFine(null);
      toast({ title: "Payment Record Deleted" });
    },
    onError: (error: Error) => {
      toast({
        title: "Unable to Delete Payment",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Calculate Statistics
  const pendingFinesList = fines.filter(f => f.status === "pending");
  const totalOutstanding = pendingFinesList.reduce(
    (acc, f) => acc + f.amount,
    0
  );
  const paidFinesList = fines.filter(f => f.status === "paid");
  const totalCollected = paidFinesList.reduce((acc, f) => acc + f.amount, 0);

  const countLateReturns = fines.filter(
    f => f.category === "late_return" && f.status === "pending"
  ).length;
  const countLostBooks = fines.filter(
    f => f.category === "lost_book" && f.status === "pending"
  ).length;
  const countDamagedBooks = fines.filter(
    f => f.category === "damaged_book" && f.status === "pending"
  ).length;
  const countExpiryFines = fines.filter(
    f => f.category === "membership_expiry" && f.status === "pending"
  ).length;

  // Filtered List per Tab
  const categoryFines = fines.filter(f => f.category === activeTab);
  const filteredFines = categoryFines.filter(f => {
    const matchesSearch =
      f.memberName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      f.memberId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      f.bookTitle?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (f.fineNo && f.fineNo.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesStatus = filterStatus === "all" || f.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const memberPayments = Object.values(
    fines.reduce(
      (acc, fine) => {
        if (!acc[fine.memberId]) {
          acc[fine.memberId] = {
            memberId: fine.memberId,
            memberName: fine.memberName,
            phone: fine.phone,
            totalPaid: 0,
            totalPending: 0,
            count: 0,
          };
        }
        if (fine.status === "paid") {
          acc[fine.memberId].totalPaid += fine.amount;
        }
        if (fine.status === "pending") {
          acc[fine.memberId].totalPending += fine.amount;
        }
        acc[fine.memberId].count += 1;
        return acc;
      },
      {} as Record<string, any>
    )
  );

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const filteredMemberPayments = memberPayments.filter(
    member =>
      member.memberName.toLowerCase().includes(normalizedSearchQuery) ||
      member.memberId.toLowerCase().includes(normalizedSearchQuery)
  );

  const handleAddSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFineForm.memberName || !newFineForm.amount) {
      toast({
        title: "Validation Error",
        description: "Member Name and Fine Amount are required.",
        variant: "destructive",
      });
      return;
    }
    addFineMutation.mutate({
      ...newFineForm,
      status: "pending",
    });
  };

  const {
    currentPage,
    totalPages,
    itemsPerPage,
    paginatedData,
    handlePageChange,
    handleItemsPerPageChange,
  } = usePagination(filteredMemberPayments);

  return (
    <DashboardLayout
      title="Payment"
      subtitle="Track, process, and collect payments for late returns, lost books, damaged books, and membership renewals."
    >
      {/* KPI Stats Strip */}
      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div className="flex items-center gap-3 rounded-xl border border-border/40 bg-card p-4 shadow-sm">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-500/10 text-red-600">
            <IndianRupee className="h-5 w-5" />
          </div>
          <div>
            <p className="text-xs font-medium text-muted-foreground">
              Pending Fines
            </p>
            <p className="mt-0.5 text-xl font-bold text-destructive">
              ₹{isLoading ? "—" : totalOutstanding.toLocaleString()}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 rounded-xl border border-border/40 bg-card p-4 shadow-sm">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-600">
            <CheckCircle2 className="h-5 w-5" />
          </div>
          <div>
            <p className="text-xs font-medium text-muted-foreground">
              Total Collected
            </p>
            <p className="mt-0.5 text-xl font-bold text-emerald-600 dark:text-emerald-400">
              ₹{isLoading ? "—" : totalCollected.toLocaleString()}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 rounded-xl border border-border/40 bg-card p-4 shadow-sm">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-500/10 text-amber-600">
            <Clock className="h-5 w-5" />
          </div>
          <div>
            <p className="text-xs font-medium text-muted-foreground">
              Pending Late Returns
            </p>
            <p className="mt-0.5 text-xl font-bold text-foreground">
              {isLoading ? "—" : countLateReturns}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 rounded-xl border border-border/40 bg-card p-4 shadow-sm">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#ca9f36]/15 text-[#8c5a12]">
            <UserX className="h-5 w-5" />
          </div>
          <div>
            <p className="text-xs font-medium text-muted-foreground">
              Expiring Memberships
            </p>
            <p className="mt-0.5 text-xl font-bold text-foreground">
              {isLoading ? "—" : countExpiryFines}
            </p>
          </div>
        </div>
      </div>

      {/* Main Section */}
      <div className="form-section">
        {/* Header Row */}
        <div className="mb-6 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <h2 className="text-lg font-bold text-foreground">
              Fine Records & Charges
            </h2>
            <p className="text-xs text-muted-foreground">
              Select a category tab below to view and collect dues
            </p>
          </div>

          <Button
            onClick={() => setShowAddDialog(true)}
            className="btn-primary w-full sm:w-auto"
          >
            <Plus className="mr-2 h-4 w-4" /> Record Manual Fine
          </Button>
        </div>

        {/* Tab Module Navigation */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="mb-6 flex min-w-0 flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="min-w-0 overflow-x-auto pb-1">
              <TabsList className="inline-flex h-auto w-max flex-nowrap gap-1 rounded-xl bg-muted/60 p-1">
                <TabsTrigger
                  value="member_payments"
                  className="gap-2 px-3 py-2 text-xs"
                >
                  <Users className="h-4 w-4 text-[#8c3c15]" />
                  Member Payments
                </TabsTrigger>

                <TabsTrigger
                  value="late_return"
                  className="gap-2 px-3 py-2 text-xs"
                >
                  <Clock className="h-4 w-4 text-amber-500" />
                  Late Return
                  {countLateReturns > 0 && (
                    <Badge
                      variant="secondary"
                      className="ml-1 border-amber-500/30 bg-amber-500/20 px-1.5 py-0 text-[10px] text-amber-600"
                    >
                      {countLateReturns}
                    </Badge>
                  )}
                </TabsTrigger>

                <TabsTrigger
                  value="lost_book"
                  className="gap-2 px-3 py-2 text-xs"
                >
                  <BookX className="h-4 w-4 text-red-500" />
                  Lost Book
                  {countLostBooks > 0 && (
                    <Badge
                      variant="secondary"
                      className="ml-1 border-red-500/30 bg-red-500/20 px-1.5 py-0 text-[10px] text-red-600"
                    >
                      {countLostBooks}
                    </Badge>
                  )}
                </TabsTrigger>

                <TabsTrigger
                  value="damaged_book"
                  className="gap-2 px-3 py-2 text-xs"
                >
                  <AlertTriangle className="h-4 w-4 text-orange-500" />
                  Damaged Book
                  {countDamagedBooks > 0 && (
                    <Badge
                      variant="secondary"
                      className="ml-1 border-orange-500/30 bg-orange-500/20 px-1.5 py-0 text-[10px] text-orange-600"
                    >
                      {countDamagedBooks}
                    </Badge>
                  )}
                </TabsTrigger>

                <TabsTrigger
                  value="membership_expiry"
                  className="gap-2 px-3 py-2 text-xs"
                >
                  <UserX className="h-4 w-4 text-[#a07a1c]" />
                  Membership Expiry
                  {countExpiryFines > 0 && (
                    <Badge
                      variant="secondary"
                      className="ml-1 border-[#ca9f36]/40 bg-[#ca9f36]/15 px-1.5 py-0 text-[10px] text-[#8c5a12]"
                    >
                      {countExpiryFines}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Filters */}
            <div className="flex w-full min-w-0 flex-col gap-2 sm:flex-row xl:w-auto xl:shrink-0">
              <div className="relative min-w-0 flex-1 xl:flex-none">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search member, book, ID..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="input-field h-9 w-full pl-9 text-xs xl:w-60"
                />
              </div>

              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="input-field h-9 w-full text-xs sm:w-36">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="waived">Waived</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* TAB 5: MEMBER PAYMENTS */}
          <TabsContent value="member_payments" className="mt-0">
            <div className="overflow-x-auto rounded-xl border border-border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Member</TableHead>
                    <TableHead>Contact Phone</TableHead>
                    <TableHead>Total Paid</TableHead>
                    <TableHead>Total Pending</TableHead>
                    <TableHead>Total Records</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    if (isLoading) {
                      return Array.from({ length: 3 }).map((_, i) => (
                        <TableRow key={i}>
                          {Array.from({ length: 5 }).map((_, j) => (
                            <TableCell key={j}>
                              <Skeleton className="h-4 w-full" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ));
                    }

                    if (filteredMemberPayments.length === 0) {
                      return (
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            className="py-12 text-center text-muted-foreground"
                          >
                            No member payments found
                          </TableCell>
                        </TableRow>
                      );
                    }

                    return paginatedData.map(member => (
                      <TableRow
                        key={member.memberId}
                        className="table-row-hover text-xs"
                      >
                        <TableCell>
                          <div>
                            <p className="font-bold text-foreground">
                              {member.memberName}
                            </p>
                            <p className="font-mono text-[11px] text-muted-foreground">
                              {member.memberId}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-muted-foreground">
                          {member.phone}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm font-bold text-emerald-600">
                            ₹{member.totalPaid}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm font-bold text-destructive">
                            ₹{member.totalPending}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {member.count} records
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ));
                  })()}
                </TableBody>
              </Table>
              <DataTablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          </TabsContent>

          {/* TAB 1: LATE RETURN */}
          <TabsContent value="late_return" className="mt-0">
            <div className="overflow-x-auto rounded-xl border border-border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Fine ID</TableHead>
                    <TableHead>Member</TableHead>
                    <TableHead>Book Details</TableHead>
                    <TableHead>Due / Return Date</TableHead>
                    <TableHead>Days Late</TableHead>
                    <TableHead>Fine Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    Array.from({ length: 4 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 8 }).map((_, j) => (
                          <TableCell key={j}>
                            <Skeleton className="h-4 w-full" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : filteredFines.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        className="py-12 text-center text-muted-foreground"
                      >
                        No late return fines found matching filter criteria
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredFines.map(fine => (
                      <TableRow
                        key={fine.id}
                        className="table-row-hover text-xs"
                      >
                        <TableCell className="font-mono font-semibold text-primary">
                          {fine.fineNo}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-bold text-foreground">
                              {fine.memberName}
                            </p>
                            <p className="font-mono text-[11px] text-muted-foreground">
                              {fine.memberId} • {fine.phone}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-foreground">
                              {fine.bookTitle}
                            </p>
                            <span className="font-mono text-[10px] text-muted-foreground">
                              {fine.bookNo}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-0.5">
                            <p className="text-[11px] text-muted-foreground">
                              Due: {fine.dueDate}
                            </p>
                            {fine.returnDate && (
                              <p className="text-[11px] font-medium text-foreground">
                                Returned: {fine.returnDate}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className="border-amber-500/30 bg-amber-500/10 text-amber-600"
                          >
                            {fine.daysLate || 0} days late
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm font-bold text-foreground">
                            ₹{fine.amount}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={`capitalize ${statusBadgeStyle[fine.status]}`}
                          >
                            {fine.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="space-x-1 text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeletingFine(fine)}
                            className="h-7 w-7 text-destructive"
                            title="Delete payment record"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                          {fine.status === "pending" ? (
                            <div className="flex justify-end gap-1">
                              <Button
                                size="sm"
                                onClick={() => setPayingFine(fine)}
                                className="btn-primary h-7 px-2.5 text-xs"
                              >
                                <Receipt className="mr-1 h-3.5 w-3.5" /> Pay
                                Fine
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => waiveMutation.mutate(fine.id)}
                                className="h-7 text-xs text-muted-foreground hover:text-foreground"
                                title="Waive Fine"
                              >
                                Waive
                              </Button>
                            </div>
                          ) : (
                            <span className="text-[11px] font-medium text-muted-foreground">
                              {fine.paidDate
                                ? `Paid on ${fine.paidDate}`
                                : "Completed"}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <DataTablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          </TabsContent>

          {/* TAB 2: LOST BOOK */}
          <TabsContent value="lost_book" className="mt-0">
            <div className="overflow-x-auto rounded-xl border border-border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Fine ID</TableHead>
                    <TableHead>Member</TableHead>
                    <TableHead>Lost Book Title</TableHead>
                    <TableHead>Reported Date</TableHead>
                    <TableHead>Replacement Charges</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 7 }).map((_, j) => (
                          <TableCell key={j}>
                            <Skeleton className="h-4 w-full" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : filteredFines.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="py-12 text-center text-muted-foreground"
                      >
                        No lost book fine records found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredFines.map(fine => (
                      <TableRow
                        key={fine.id}
                        className="table-row-hover text-xs"
                      >
                        <TableCell className="font-mono font-semibold text-primary">
                          {fine.fineNo}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-bold text-foreground">
                              {fine.memberName}
                            </p>
                            <p className="font-mono text-[11px] text-muted-foreground">
                              {fine.memberId} • {fine.phone}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-foreground">
                              {fine.bookTitle}
                            </p>
                            <span className="font-mono text-[10px] text-muted-foreground">
                              {fine.bookNo}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {fine.createdDate}
                        </TableCell>
                        <TableCell>
                          <div>
                            <span className="text-sm font-bold text-red-600 dark:text-red-400">
                              ₹{fine.amount}
                            </span>
                            <p className="text-[10px] text-muted-foreground">
                              Includes processing fee
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={`capitalize ${statusBadgeStyle[fine.status]}`}
                          >
                            {fine.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="space-x-1 text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeletingFine(fine)}
                            className="h-7 w-7 text-destructive"
                            title="Delete payment record"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                          {fine.status === "pending" ? (
                            <Button
                              size="sm"
                              onClick={() => setPayingFine(fine)}
                              className="btn-primary h-7 px-2.5 text-xs"
                            >
                              <Receipt className="mr-1 h-3.5 w-3.5" /> Collect
                              Replacement Charge
                            </Button>
                          ) : (
                            <span className="text-[11px] font-medium text-muted-foreground">
                              Paid on {fine.paidDate || fine.createdDate}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <DataTablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          </TabsContent>

          {/* TAB 3: DAMAGED BOOK */}
          <TabsContent value="damaged_book" className="mt-0">
            <div className="overflow-x-auto rounded-xl border border-border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Fine ID</TableHead>
                    <TableHead>Member</TableHead>
                    <TableHead>Book Title</TableHead>
                    <TableHead>Damage Severity</TableHead>
                    <TableHead>Assessment Remarks</TableHead>
                    <TableHead>Penalty Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 8 }).map((_, j) => (
                          <TableCell key={j}>
                            <Skeleton className="h-4 w-full" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : filteredFines.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        className="py-12 text-center text-muted-foreground"
                      >
                        No damaged book penalty records found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredFines.map(fine => (
                      <TableRow
                        key={fine.id}
                        className="table-row-hover text-xs"
                      >
                        <TableCell className="font-mono font-semibold text-primary">
                          {fine.fineNo}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-bold text-foreground">
                              {fine.memberName}
                            </p>
                            <p className="font-mono text-[11px] text-muted-foreground">
                              {fine.memberId}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-foreground">
                              {fine.bookTitle}
                            </p>
                            <span className="font-mono text-[10px] text-muted-foreground">
                              {fine.bookNo}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={`capitalize ${damageBadgeStyle[fine.damageSeverity || "minor"]}`}
                          >
                            {fine.damageSeverity || "minor"} damage
                          </Badge>
                        </TableCell>
                        <TableCell
                          className="max-w-[200px] truncate text-muted-foreground"
                          title={fine.remarks}
                        >
                          {fine.remarks || "Book damage assessment"}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm font-bold text-foreground">
                            ₹{fine.amount}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={`capitalize ${statusBadgeStyle[fine.status]}`}
                          >
                            {fine.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="space-x-1 text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeletingFine(fine)}
                            className="h-7 w-7 text-destructive"
                            title="Delete payment record"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                          {fine.status === "pending" ? (
                            <Button
                              size="sm"
                              onClick={() => setPayingFine(fine)}
                              className="btn-primary h-7 px-2.5 text-xs"
                            >
                              <Receipt className="mr-1 h-3.5 w-3.5" /> Collect
                              Penalty
                            </Button>
                          ) : (
                            <span className="text-[11px] font-medium text-muted-foreground">
                              Paid on {fine.paidDate || fine.createdDate}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <DataTablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          </TabsContent>

          {/* TAB 4: MEMBERSHIP EXPIRY */}
          <TabsContent value="membership_expiry" className="mt-0">
            <div className="overflow-x-auto rounded-xl border border-border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Fine ID</TableHead>
                    <TableHead>Member</TableHead>
                    <TableHead>Contact Phone</TableHead>
                    <TableHead>Expiry Date</TableHead>
                    <TableHead>Annual Renewal Fee</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 7 }).map((_, j) => (
                          <TableCell key={j}>
                            <Skeleton className="h-4 w-full" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : filteredFines.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="py-12 text-center text-muted-foreground"
                      >
                        No membership expiry renewal records found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredFines.map(fine => (
                      <TableRow
                        key={fine.id}
                        className="table-row-hover text-xs"
                      >
                        <TableCell className="font-mono font-semibold text-primary">
                          {fine.fineNo}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-bold text-foreground">
                              {fine.memberName}
                            </p>
                            <p className="font-mono text-[11px] text-muted-foreground">
                              {fine.memberId}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-muted-foreground">
                          {fine.phone}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 font-mono font-semibold text-amber-600 dark:text-amber-400">
                            <Calendar className="h-3.5 w-3.5" />
                            {fine.expiryDate || fine.createdDate}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm font-bold text-foreground">
                            ₹{fine.amount}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={`capitalize ${statusBadgeStyle[fine.status]}`}
                          >
                            {fine.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="space-x-1 text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeletingFine(fine)}
                            className="h-7 w-7 text-destructive"
                            title="Delete payment record"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                          {fine.status === "pending" ? (
                            <Button
                              size="sm"
                              onClick={() => setPayingFine(fine)}
                              className="btn-primary h-7 px-2.5 text-xs"
                            >
                              <RotateCcw className="mr-1 h-3.5 w-3.5" /> Renew
                              Membership (+1 Yr)
                            </Button>
                          ) : (
                            <span className="text-[11px] font-semibold text-emerald-600">
                              Renewed on {fine.paidDate || fine.createdDate}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <DataTablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Collect Fine / Payment Modal */}
      <Dialog
        open={!!payingFine}
        onOpenChange={open => !open && setPayingFine(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base text-primary">
              <Receipt className="h-5 w-5 text-primary" /> Collect Fine /
              Renewal Payment
            </DialogTitle>
            <DialogDescription className="text-xs">
              Record fee collection for {payingFine?.memberName} (
              {payingFine?.fineNo}).
            </DialogDescription>
          </DialogHeader>

          {payingFine && (
            <div className="space-y-4 py-2 text-xs">
              <div className="space-y-1.5 rounded-lg border border-border/50 bg-muted/40 p-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Member:</span>
                  <span className="font-bold text-foreground">
                    {payingFine.memberName} ({payingFine.memberId})
                  </span>
                </div>
                {payingFine.bookTitle && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Book:</span>
                    <span className="font-medium text-foreground">
                      {payingFine.bookTitle}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Category:</span>
                  <span className="font-semibold capitalize text-primary">
                    {payingFine.category.replace("_", " ")}
                  </span>
                </div>
                <div className="flex justify-between border-t border-border/40 pt-1">
                  <span className="font-semibold text-foreground">
                    Total Dues:
                  </span>
                  <span className="text-lg font-extrabold text-emerald-600">
                    ₹{payingFine.amount}
                  </span>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Select Payment Method</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger className="input-field">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Cash">Cash Payment</SelectItem>
                    <SelectItem value="UPI / QR">UPI / Digital QR</SelectItem>
                    <SelectItem value="Card">Debit / Credit Card</SelectItem>
                    <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPayingFine(null)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="btn-primary"
              disabled={payMutation.isPending}
              onClick={() =>
                payingFine &&
                payMutation.mutate({ id: payingFine.id, method: paymentMethod })
              }
            >
              {payMutation.isPending
                ? "Processing..."
                : "Confirm & Print Receipt"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!deletingFine}
        onOpenChange={open => !open && setDeletingFine(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Payment Record</DialogTitle>
            <DialogDescription>
              Delete {deletingFine?.fineNo || "this payment record"} for{" "}
              {deletingFine?.memberName}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingFine(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() =>
                deletingFine && deleteMutation.mutate(deletingFine.id)
              }
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete Record"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Manual Fine Modal */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Record New Fine or Penalty</DialogTitle>
            <DialogDescription>
              Manually enter a fine for late return, lost book, damage, or
              renewal fee.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleAddSubmit} className="space-y-4 py-2 text-xs">
            <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 sm:gap-4">
              <div className="space-y-1">
                <Label>Category *</Label>
                <Select
                  value={newFineForm.category}
                  onValueChange={v =>
                    setNewFineForm({
                      ...newFineForm,
                      category: v as FineItem["category"],
                    })
                  }
                >
                  <SelectTrigger className="input-field">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="late_return">Late Return</SelectItem>
                    <SelectItem value="lost_book">Lost Book</SelectItem>
                    <SelectItem value="damaged_book">Damaged Book</SelectItem>
                    <SelectItem value="membership_expiry">
                      Membership Expiry
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label>Amount (₹) *</Label>
                <Input
                  type="number"
                  placeholder="100"
                  className="input-field"
                  value={newFineForm.amount}
                  onChange={e =>
                    setNewFineForm({
                      ...newFineForm,
                      amount: Number(e.target.value),
                    })
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 sm:gap-4">
              <div className="space-y-1">
                <Label>Member *</Label>
                <Select
                  value={newFineForm.memberId}
                  onValueChange={memberId => {
                    const member = members.find(m => m.memberId === memberId);
                    if (member) {
                      setNewFineForm({
                        ...newFineForm,
                        memberId: member.memberId,
                        memberName: member.name,
                        phone: member.phone,
                      });
                    }
                  }}
                >
                  <SelectTrigger className="input-field">
                    <SelectValue placeholder="Select Member" />
                  </SelectTrigger>
                  <SelectContent>
                    {members
                      .filter(m => m.status === "active")
                      .map(member => (
                        <SelectItem key={member.id} value={member.memberId}>
                          {member.memberId} — {member.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label>Member Name</Label>
                <Input
                  className="input-field bg-muted/30"
                  value={newFineForm.memberName}
                  readOnly
                  placeholder="Auto-filled from Member"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 sm:gap-4">
              <div className="space-y-1">
                <Label>Book (Optional)</Label>
                <Select
                  value={newFineForm.bookNo}
                  onValueChange={bookNo => {
                    const book = books.find(b => b.bookNo === bookNo);
                    if (book) {
                      setNewFineForm({
                        ...newFineForm,
                        bookNo: book.bookNo,
                        bookTitle: book.title,
                      });
                    }
                  }}
                >
                  <SelectTrigger className="input-field">
                    <SelectValue placeholder="Select Book" />
                  </SelectTrigger>
                  <SelectContent>
                    {books.map(b => (
                      <SelectItem key={b.id} value={b.bookNo}>
                        {b.bookNo} — {b.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label>Phone Number</Label>
                <Input
                  className="input-field bg-muted/30"
                  value={newFineForm.phone}
                  readOnly
                  placeholder="Auto-filled from Member"
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label>Remarks / Notes</Label>
              <Input
                placeholder="Reason or damage details..."
                className="input-field"
                value={newFineForm.remarks}
                onChange={e =>
                  setNewFineForm({ ...newFineForm, remarks: e.target.value })
                }
              />
            </div>

            <DialogFooter className="mt-4 flex flex-col-reverse gap-2 border-t pt-3 sm:flex-row sm:gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddDialog(false)}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={addFineMutation.isPending}
                className="btn-primary w-full sm:w-auto"
              >
                {addFineMutation.isPending ? "Creating..." : "Save Fine Record"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
